/**
 * Authentication Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { authService } from '../services/authService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  LoginPayload,
  SendOtpPayload,
  VerifyOtpPayload,
  UpdateProfilePayload,
  ResetChangePasswordPayload,
  ChangePasswordPayload,
} from '../types/auth.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get user profile hook
 */
export function useGetProfile(
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.profile],
    queryFn: () => authService.getProfile(),
    // Always refetch on mount to ensure fresh data after login
    refetchOnMount: true,
    // Don't use stale data - always fetch fresh
    staleTime: 0,
    ...options,
  });
}

/**
 * Login mutation hook
 */
export function useLogin(
  options?: UseMutationOptions<any, any, LoginPayload, any>
) {
  return useApiMutation<any, any, LoginPayload, any>({
    mutationFn: ((data: LoginPayload) => authService.login(data)) as any,
    ...options,
  });
}

/**
 * Logout mutation hook
 */
export function useLogout(
  options?: UseMutationOptions<any, any, void, any>
) {
  return useApiMutation<any, any, void, any>({
    mutationFn: (() => authService.logout()) as any,
    onSuccess: () => {
      // Invalidate profile query
      getQueryClient().invalidateQueries({ queryKey: [queryTags.profile] });
    },
    ...options,
  });
}

/**
 * Send OTP mutation hook
 */
export function useSendOtp(
  options?: UseMutationOptions<any, any, SendOtpPayload, any>
) {
  return useApiMutation<any, any, SendOtpPayload, any>({
    mutationFn: ((data: SendOtpPayload) => authService.sendOtp(data)) as any,
    ...options,
  });
}

/**
 * Verify OTP mutation hook
 */
export function useVerifyOtp(
  options?: UseMutationOptions<any, any, VerifyOtpPayload, any>
) {
  return useApiMutation<any, any, VerifyOtpPayload, any>({
    mutationFn: ((data: VerifyOtpPayload) => authService.verifyOtp(data)) as any,
    ...options,
  });
}

/**
 * Update profile mutation hook
 */
export function useUpdateProfile(
  options?: UseMutationOptions<any, any, UpdateProfilePayload, any>
) {
  return useApiMutation<any, any, UpdateProfilePayload, any>({
    mutationFn: ((data: UpdateProfilePayload) => authService.updateProfile(data)) as any,
    onSuccess: () => {
      // Invalidate profile query
      getQueryClient().invalidateQueries({ queryKey: [queryTags.profile] });
    },
    ...options,
  });
}

/**
 * Reset/Change password mutation hook
 */
export function useResetChangePassword(
  options?: UseMutationOptions<any, any, ResetChangePasswordPayload, any>
) {
  return useApiMutation<any, any, ResetChangePasswordPayload, any>({
    mutationFn: ((data: ResetChangePasswordPayload) =>
      authService.resetChangePassword(data)) as any,
    ...options,
  });
}

/**
 * Send OTP for forgot password hook
 */
export function useSendForgotPasswordOtp(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((email: string) => authService.sendForgotPasswordOtp(email)) as any,
    ...options,
  });
}

/**
 * Verify OTP for forgot password hook
 */
export function useVerifyForgotPasswordOtp(
  options?: UseMutationOptions<any, any, { email: string; otp: string }, any>
) {
  return useApiMutation<any, any, { email: string; otp: string }, any>({
    mutationFn: ((data: { email: string; otp: string }) =>
      authService.verifyForgotPasswordOtp(data.email, data.otp)) as any,
    ...options,
  });
}

/**
 * Reset password (forgot password flow) hook
 */
export function useResetPassword(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((newPassword: string) => authService.resetPassword(newPassword)) as any,
    ...options,
  });
}

/**
 * Change password (for authenticated users) hook
 */
export function useChangePassword(
  options?: UseMutationOptions<any, any, ChangePasswordPayload, any>
) {
  return useApiMutation<any, any, ChangePasswordPayload, any>({
    mutationFn: ((data: ChangePasswordPayload) => authService.changePassword(data)) as any,
    ...options,
  });
}

